import { useRef, useState, VFC } from 'react'
import {
  addMessageTemplate,
  updateMessageTemplate,
  useMessageTemplates,
} from 'src/api/messageTemplate'
import { CloseIcon, PlusCircleIcon } from 'src/assets/svgs'
import { asModal, ModalProps } from 'src/components/hoc/asModal'
import { useErrorMessageModal } from 'src/hooks/useModal'
import {
  truewhite,
  _messagebuttonbg,
  _messagetemplateborder,
  _messagetemplatebuttondisabled,
  _messagetemplatebuttonsaved,
} from 'src/styles/colors'
import { fontWeightBold, fontWeightMedium } from 'src/styles/font'
import { flexCenter } from 'src/styles/mixins'
import styled, { css } from 'styled-components'
import { Button, ButtonsDiv } from '../styles'

type MessageTemplateProps = {
  insert: (template: string) => void
}
const MessageTemplate: VFC<ModalProps<MessageTemplateProps>> = ({
  insert,
  close,
}) => {
  const { data } = useMessageTemplates()
  const [editingNew, setEditingNew] = useState(false)
  const templatesEl = useRef<HTMLDivElement>(null)
  const scrollTo = (top: number) => {
    if (!templatesEl.current) return
    templatesEl.current.scrollTo({ top, behavior: 'smooth' })
  }
  const { open } = useErrorMessageModal()

  return (
    <Layout>
      <Templates ref={templatesEl}>
        {data?.map(({ id, template }, idx) => (
          <Template
            key={id}
            label={`定型文${idx + 1}`}
            template={template}
            insert={insert}
            close={close}
            onSave={async (template: string) => {
              const res = await updateMessageTemplate(id, template)
              if (res.error) {
                open({ message: res.error.errorMessages?.join('\n') })
                return
              }
            }}
            scrollTo={scrollTo}
          />
        ))}
        {editingNew ? (
          <Template
            label="新規定型文"
            insert={insert}
            close={close}
            onSave={async (template: string) => {
              const res = await addMessageTemplate(template)
              if (res.response?.status === 422) {
                open({ message: '定型文は10件まで登録可能です。' })
                return
              }
              if (res.error) {
                open({ message: res.error.errorMessages?.join('\n') })
                return
              }
              setEditingNew(false)
            }}
            scrollTo={scrollTo}
          />
        ) : (
          <TemplateAddButton onClick={() => setEditingNew(true)}>
            <PlusCircleIcon />
            定型文を追加する
          </TemplateAddButton>
        )}
      </Templates>
      <CloseButton onClick={close}>
        <CloseIcon />
        CLOSE
      </CloseButton>
    </Layout>
  )
}
export const MessageTemplateModal = asModal(MessageTemplate, {
  inescapable: true,
  scrollable: true,
})

type TemplateProps = {
  label: string
  template?: string
  insert: (template: string) => void
  close: VoidFunction
  onSave: (template: string) => Promise<any>
  scrollTo: (top: number) => void
}
const Template: VFC<TemplateProps> = ({
  label,
  template = '',
  onSave,
  insert,
  close,
  scrollTo,
}) => {
  const [newTemplate, setNewTemplate] = useState(template)
  const [saved, setSaved] = useState(false)
  const hasChanged = template !== newTemplate
  return (
    <TemplateContainer>
      <h4>{label}</h4>
      <TemplateTextarea
        value={newTemplate}
        onChange={({ target: { value } }) => setNewTemplate(value)}
        onFocus={({ target }) => scrollTo(target.offsetTop - 60)}
      />
      <ButtonsDiv>
        <EditButton
          onMouseDown={async (e) => {
            e.preventDefault()
            await onSave(newTemplate)
            setSaved(true)
          }}
          disabled={!hasChanged}
          saved={!hasChanged && saved}
        >
          {!hasChanged && saved ? '保存済み' : '編集した文章を保存'}
        </EditButton>
        <Button
          onMouseDown={(e) => {
            e.preventDefault()
            insert(newTemplate)
            close()
          }}
        >
          挿入
        </Button>
      </ButtonsDiv>
    </TemplateContainer>
  )
}
const EditButton = styled(Button)<{ saved: boolean }>`
  padding: 6px 12px;

  :disabled {
    color: ${_messagebuttonbg};
    background: ${_messagetemplatebuttondisabled};
    ${({ saved }) =>
      saved &&
      css`
        background: ${_messagetemplatebuttonsaved};
      `}
  }
`
const Templates = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  height: 100%;
  padding-bottom: 25vh;
  overflow-y: auto;
  margin-left: -32px;
  margin-bottom: -38px;
  > * {
    margin-left: 32px;
    margin-bottom: 38px;
  }
`
const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  color: ${truewhite};
  font-weight: ${fontWeightBold};
  display: flex;
  align-items: center;
  svg {
    height: 1.2em;
  }
`
const Layout = styled.div`
  position: relative;
  width: 80vw;
  height: 80vh;
  padding: 32px;
  border-radius: 10px;

  h3 {
    font-size: 18px;
    font-weight: ${fontWeightBold};
  }
  p {
    margin-top: 10px;
    font-weight: ${fontWeightMedium};
  }
  ${ButtonsDiv} {
    margin-top: 8px;
  }
`

const templateStyle = css`
  width: 347px;
  height: 140px;
  border-radius: 8px;
  background-color: ${truewhite};
  border: 1px solid ${_messagetemplateborder};
`
const TemplateContainer = styled.div`
  h4 {
    margin-left: 8px;
    margin-bottom: 12px;
    font-weight: ${fontWeightBold};
    color: ${truewhite};
  }
`

const TemplateTextarea = styled.textarea`
  ${templateStyle}
  padding: 8px 10px;
  resize: none;
  transition: height 0.5s;
  :focus {
    height: 60vh;
  }
`

const TemplateAddButton = styled.button`
  ${templateStyle};
  margin-top: 28px;
  color: ${_messagebuttonbg};
  font-weight: ${fontWeightBold};
  ${flexCenter};
  flex-direction: column;
  svg {
    margin-bottom: 4px;
  }
`
